import i18n from '@/i18n/config';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

import { createCdnProvider, updateCdnProvider, deleteCdnProvider, type CdnProviderForm } from '@/store';
import type { CdnProviderItem } from '@/types';

export type { CdnProviderForm };

export function useCdnProviderEdit() {
  const [, updateProvider] = useAtom(updateCdnProvider);
  const { isOpen: isEditOpen, onOpen: onEditOpen, onOpenChange: onEditOpenChange } = useDisclosure();
  const [editingProviderId, setEditingProviderId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState<CdnProviderForm>({
    name: '',
    base_url: ''
  });

  const openEdit = useCallback(
    (provider: CdnProviderItem) => {
      setEditingProviderId(provider.id);
      setEditForm({
        name: provider.name,
        base_url: provider.base_url
      });
      onEditOpen();
    },
    [onEditOpen]
  );

  const saveEdit = useCallback(async () => {
    if (!editingProviderId) return;

    try {
      setIsSaving(true);
      await updateProvider({ id: editingProviderId, formData: editForm });
      toast.success(i18n.t('copy.cdn_provider_updated_successfully'));
      setIsSaving(false);
      onEditOpenChange();
      setEditingProviderId(null);
    } catch (error) {
      setIsSaving(false);
      // Error is handled by axios interceptor
    }
  }, [editingProviderId, editForm, onEditOpenChange, updateProvider]);

  return {
    isEditOpen,
    editingProviderId,
    isSaving,
    editForm,
    setEditForm,
    openEdit,
    saveEdit,
    onEditOpenChange
  };
}

export function useCdnProviderCreate() {
  const [, createProvider] = useAtom(createCdnProvider);
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onOpenChange: onCreateOpenChange } = useDisclosure();
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CdnProviderForm>({
    name: '',
    base_url: ''
  });

  const openCreate = useCallback(() => {
    setCreateForm({ name: '', base_url: '' });
    onCreateOpen();
  }, [onCreateOpen]);

  const saveCreate = useCallback(async () => {
    try {
      setIsCreating(true);
      await createProvider(createForm);
      toast.success(i18n.t('copy.cdn_provider_created_successfully'));
      setIsCreating(false);
      onCreateOpenChange();
    } catch (error) {
      setIsCreating(false);
      // Error is handled by axios interceptor
    }
  }, [createForm, onCreateOpenChange, createProvider]);

  return {
    isCreateOpen,
    isCreating,
    createForm,
    setCreateForm,
    openCreate,
    saveCreate,
    onCreateOpenChange
  };
}

export function useCdnProviderDelete() {
  const [, deleteProvider] = useAtom(deleteCdnProvider);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onOpenChange: onDeleteOpenChange } = useDisclosure();
  const [deletingProviderId, setDeletingProviderId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = useCallback(
    (provider: CdnProviderItem) => {
      setDeletingProviderId(provider.id);
      onDeleteOpen();
    },
    [onDeleteOpen]
  );

  const confirmDelete = useCallback(async () => {
    if (!deletingProviderId) return;

    try {
      setIsDeleting(true);
      await deleteProvider(deletingProviderId);
      toast.success(i18n.t('copy.cdn_provider_deleted_successfully'));
      setIsDeleting(false);
      onDeleteOpenChange();
      setDeletingProviderId(null);
    } catch (error) {
      setIsDeleting(false);
      // Error is handled by axios interceptor
    }
  }, [deletingProviderId, onDeleteOpenChange, deleteProvider]);

  return {
    isDeleteOpen,
    deletingProviderId,
    isDeleting,
    openDelete,
    confirmDelete,
    onDeleteOpenChange
  };
}
